import React from 'react';

import { ia } from 'lib/helpers/utility';

import Actions from 'components/practice/charts/Overview/widgets/components/Actions';
import { MEDICATION_STATUS } from 'components/practice/charts/Overview/widgets/lib/medicationsCols';
import Dot from 'components/shared/Dot/Dot';
import { productNdcGroupKeyCreator } from 'components/shared/Forms/Custom/ProviderMedications/hooks/useMedicationProductNdcGrouping';
import {
  getGroupedMedicationData,
  getMedicationDoseText
} from 'components/shared/Forms/Custom/ProviderMedications/lib/medicationGroupHelpers';
import Icon from 'components/shared/Icon/Icon';
import Status from 'components/shared/Status/Status';

const disableRow = (params) => {
  const isOwner = params?.data?.patient_id === params?.data?.created_by;
  const isReadOnly = Boolean(params?.data?.veradigm_id);

  return !isOwner || isReadOnly ? { 'pointer-events': 'none', opacity: '0.4' } : {};
};

const MedicationCellContent = ({ params, props }) => {
  const medication = getGroupedMedicationData(params);

  if (!medication) {
    return null;
  }

  const isMedicationActive = medication?.status === MEDICATION_STATUS.ACTIVE;
  const isPatient = medication?.patient_id === medication?.created_by;

  return (
    <div className="flex h-full w-full items-center gap-2">
      <Icon
        icon="document-preview-mono"
        shade={600}
        color="neutral"
        onClick={() => props.setShowMedicationModal({ open: true, data: medication })}
      />
      <Dot size="md" color={isMedicationActive ? 'success' : 'danger'} />

      <span className="truncate text-sm font-400 text-neutral-900">{medication?.medication}</span>

      {isPatient && <Icon icon="patient-response-mono" shade={600} />}
    </div>
  );
};

export default (props) => [
  {
    field: 'medication',
    headerName: 'Medication',
    cellRenderer: 'agGroupCellRenderer',
    showRowGroup: true,
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params) => <MedicationCellContent params={params} props={props} />
    },
    headerCheckboxSelection: props?.showCheckboxes,
    checkboxSelection: (params) => !params?.node?.group && props?.showCheckboxes,
    flex: 2
  },
  {
    field: 'status',
    headerName: 'Status',
    cellRenderer: (params) => {
      const medication = getGroupedMedicationData(params);

      if (!medication?.status) return null;

      const status = medication?.status;
      return <Status status={status} />;
    }
  },
  {
    field: 'practice.name',
    headerName: 'Practice',
    valueGetter: (params) => getGroupedMedicationData(params)?.practice?.name,
    valueFormatter: ({ value }) => (!!value ? value : 'N/A')
  },
  {
    field: 'dose',
    headerName: 'Dose',
    cellRenderer: (params) => {
      const medication = getGroupedMedicationData(params);

      if (!medication) {
        return null;
      }
      const content = getMedicationDoseText(medication);
      return content ? <span>{content}</span> : 'N/A';
    }
  },
  {
    field: 'product_ndc',
    headerName: 'NDC Code',
    keyCreator: productNdcGroupKeyCreator,
    valueGetter: (params) => getGroupedMedicationData(params)?.product_ndc,
    valueFormatter: ({ value }) => (!!value ? value : 'N/A')
  },
  {
    field: 'prescriber.label',
    headerName: 'Prescriber',
    valueGetter: (params) => getGroupedMedicationData(params)?.prescriber?.label,
    valueFormatter: ({ value }) => (!!value ? value : 'N/A')
  },
  {
    field: 'start_date',
    headerName: 'Start date',
    valueGetter: (params) => getGroupedMedicationData(params)?.start_date,
    valueFormatter: ({ value }) => (value ? value : 'N/A')
  },
  {
    field: 'end_date',
    headerName: 'End date',
    valueGetter: (params) => getGroupedMedicationData(params)?.end_date,
    valueFormatter: ({ value }) => (value ? value : 'N/A')
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: (params) => {
      if (params?.node?.group || !params?.data) {
        return null;
      }

      return <Actions data={params?.data} customProps={props} />;
    },
    maxWidth: 100,
    pinned: 'right',
    cellStyle: (params) => disableRow(params)
  }
];

export const disableIfNotOwner = ({ patientId, data }) => {
  return patientId !== data?.created_by;
};
export const getPrefilledPatientPractice = ({ data = [], formik = {} }) => {
  if (!ia(data)) {
    return [];
  }

  if (data?.length === 1 && !formik?.values?.practice_id) {
    const practice = data[0]?.practice;
    const practiceOption = { value: practice?.id, label: practice?.name };
    formik?.setFieldValue('practice_id', practiceOption?.value);

    return practiceOption;
  }
  if (data?.length > 1 || formik?.values?.practice_id) {
    const selectedPractice = data.find(
      (item) => item?.practice?.id === formik?.values?.practice_id
    )?.practice;

    return selectedPractice
      ? { value: formik?.values?.practice_id, label: selectedPractice?.name }
      : null;
  }

  return null;
};

export const getPatientPracticeOptions = (options) => {
  return options?.map((item) => ({ value: item?.practice?.id, label: item?.practice?.name }));
};
